import { getEvmClient } from '@1delta/providers'
import type { Address } from 'viem'
import { YieldFetcher } from '../../types'
import { annualizeGrowthPercent } from '../annualizeRate'

/**
 * PrimeStaking psXDC (Prime Numbers Labs) — XDC liquid staking, chainId
 * 50. The V3 vault (`0x98D916…C4Ba`) is an ERC-4626-style share token
 * whose value accrues through the share price (NAV) as masternode
 * rewards settle.
 *
 * **Yield source: subgraph reward stream, guarded, with a documented
 * base-rate fallback.** Two live options exist; neither is reliable
 * today, so the fetcher tries the subgraph and falls back:
 *
 *  1. *On-chain NAV rate-delta* (the stCELO approach — see `./stcelo.ts`).
 *     The V3 vault currently pins `convertToAssets(1e18)` at exactly
 *     `1e18` (`totalAssets == totalSupply`); rewards are not yet
 *     reflected in the share price, so a rate-delta yields a flat 0. Once
 *     the NAV begins to move this becomes the right primary source.
 *  2. *The `/liquidstaking` subgraph* (subgraph.primenumbers.xyz),
 *     implemented below. It exposes a protocol-wide `RewardAdded` event
 *     stream; we annualise the trailing-window reward total against the
 *     on-chain TVL (`totalAssets()`):
 *
 *       apr = (rewardsInWindow / totalAssets) * (yearSeconds / window) * 100
 *
 *     **Caveat — the stream is currently placeholder noise.** In
 *     practice it is dominated by `1 XDC` marker postings (~monthly) plus
 *     occasional one-off entries (e.g. a single 210k XDC posting),
 *     against an ~80M XDC TVL — annualising gives ~0.3%, far below the
 *     real staking yield. So the computed candidate is sanity-banded
 *     ({@link MIN_SANE_APR}–{@link MAX_SANE_APR}); when it falls outside
 *     the band (as it does today) we fall back to the documented base
 *     APY. If PrimeStaking later emits real per-period reward accrual,
 *     the candidate will pass the band and be used automatically.
 *
 * Docs: https://docs.primestaking.xyz/
 */

/** Documented psXDC base staking APY (percent) — the fallback. */
const PRIME_STAKING_BASE_APY = 4.5

const CHAIN_ID = '50'
/** psXDC V3 vault — exposes `totalAssets()` (TVL in native-XDC wei). */
const VAULT: Address = '0x98d916f5773ac0482b49856f2659d6c32114c4ba'
const SUBGRAPH_URL =
  'https://subgraph.primenumbers.xyz/subgraphs/name/liquidstaking'

/** Trailing window over which `RewardAdded` totals are annualised. The
 *  stream is sparse (~monthly), so a wide window is needed to capture
 *  more than a couple of postings. */
const WINDOW_SECONDS = 180 * 24 * 60 * 60

/** Plausible psXDC APR band. A subgraph-derived candidate outside this
 *  range is treated as noise and the documented base rate is used. */
const MIN_SANE_APR = 1
const MAX_SANE_APR = 12

const TOTAL_ASSETS_ABI = [
  {
    name: 'totalAssets',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
] as const

interface SubgraphResult {
  data?: {
    _meta?: { block?: { timestamp?: string } }
    rewardAddeds?: { reward: string; blockTimestamp: string }[]
  }
}

/** Sum `RewardAdded.reward` (wei) over the trailing window and return it
 *  alongside the reference timestamp. Throws on any query/shape failure
 *  so the caller can fall back. */
const fetchWindowedRewards = async (): Promise<{
  rewardsWei: bigint
  refTs: number
}> => {
  const res = await fetch(SUBGRAPH_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query:
        '{ _meta { block { timestamp } } rewardAddeds(first: 1000, orderBy: blockTimestamp, orderDirection: desc) { reward blockTimestamp } }',
    }),
  })
  if (!res.ok) throw new Error(`subgraph HTTP ${res.status}`)
  const json = (await res.json()) as SubgraphResult
  const rows = json.data?.rewardAddeds
  const metaTs = Number(json.data?._meta?.block?.timestamp)
  if (!rows || !Number.isFinite(metaTs)) throw new Error('subgraph shape')

  const cutoff = metaTs - WINDOW_SECONDS
  let rewardsWei = 0n
  for (const r of rows) {
    if (Number(r.blockTimestamp) >= cutoff) rewardsWei += BigInt(r.reward)
  }
  return { rewardsWei, refTs: metaTs }
}

const computeApr = async (): Promise<number> => {
  const [{ rewardsWei }, totalAssets] = await Promise.all([
    fetchWindowedRewards(),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (getEvmClient(CHAIN_ID) as any).readContract({
      address: VAULT,
      abi: TOTAL_ASSETS_ABI,
      functionName: 'totalAssets',
    }) as Promise<bigint>,
  ])

  if (totalAssets <= 0n) throw new Error('zero TVL')

  // Float math is fine here: the ratio is well below 1 and we only need
  // a percentage to ~2dp.
  const apr = annualizeGrowthPercent(
    Number(rewardsWei) / Number(totalAssets),
    WINDOW_SECONDS,
  )
  if (!Number.isFinite(apr)) throw new Error(`bad apr ${apr}`)
  return apr
}

export const psxdcFetcher: YieldFetcher = {
  label: 'PSXDC',
  fetch: async () => {
    try {
      const apr = await computeApr()
      if (apr >= MIN_SANE_APR && apr <= MAX_SANE_APR) return { psXDC: apr }
      // Out-of-band → the reward stream is not a real yield signal yet.
    } catch {
      // Subgraph/RPC failure → fall back.
    }
    return { psXDC: PRIME_STAKING_BASE_APY }
  },
}
